/**
 * BriefingPanel
 *
 * Calls /v2/briefing/daily and renders a structured summary of the
 * market state across tracked symbols. Designed to be the centrepiece
 * of the markets page — shows what DWLF computes that nobody else does.
 *
 * Wraps content in the shared Panel chrome and exposes a refresh()
 * method via ref so the page-level refresh button can re-fetch.
 */
/* eslint-disable eqeqeq */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Skeleton,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { TrendingUp, TrendingDown, Remove, Close, HelpOutline } from '@mui/icons-material';
import { fetchBriefing } from '../marketsSlice';
import Panel from './Panel';
import BriefingDepth from './BriefingDepth';

// Days within which a state transition or custom event fire is considered
// "fresh". Exported so the RegimeHeatmap and any future regime-aware UI
// use the exact same freshness window — if this ever drifts between
// components, the "NEW" tag would render inconsistently between panels.
export const FRESH_WINDOW_DAYS = 3;

// Returns true if the given ISO date string (YYYY-MM-DD or full ISO) is
// within FRESH_WINDOW_DAYS of today. Exported alongside FRESH_WINDOW_DAYS
// for the same single-source-of-truth reason.
export const isFresh = (dateString) => {
  if (!dateString) {return false;}
  const t = new Date(dateString).getTime();
  if (Number.isNaN(t)) {return false;}
  const cutoff = Date.now() - FRESH_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  return t >= cutoff;
};

// Pick a colour palette for a regime state. We track the colour as an RGB
// triplet so we can compose rgba() strings cleanly at any opacity, rather
// than naively appending a hex alpha suffix to whatever string we have
// (which produces invalid CSS like `var(--dwlf-ink-50)33`).
//
// Exported so the RegimeHeatmap (and any future regime-aware UI) can
// derive identical colors without duplicating the keyword logic.
export const getStatePalette = (state) => {
  const lower = (state || '').toLowerCase();
  if (lower.includes('up') || lower.includes('bull') || lower.includes('strong_up')) {
    return { rgb: '63, 185, 80', Icon: TrendingUp };
  }
  if (lower.includes('down') || lower.includes('bear') || lower.includes('strong_down')) {
    return { rgb: '248, 81, 73', Icon: TrendingDown };
  }
  return { rgb: '255, 255, 255', Icon: Remove };
};

// `dimension` is one of 'trend' | 'mom' | 'cycle' — rendered as a small
// faded prefix inside the badge so users can tell at a glance which
// regime model the state belongs to. Without this, three badges in a
// row look like a flat list of unrelated tags.
const TrendBadge = ({ dimension, state, fresh }) => {
  const { rgb, Icon } = getStatePalette(state);
  const isNeutral = rgb === '255, 255, 255';

  // Neutral states use a dimmer base alpha so they don't overpower
  // bullish/bearish badges in the same row.
  const textAlpha = isNeutral ? 0.5 : 1;
  const bgAlpha = isNeutral ? 0.05 : 0.1;
  const borderAlpha = fresh ? (isNeutral ? 0.4 : 0.6) : (isNeutral ? 0.15 : 0.2);

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        backgroundColor: `rgba(${rgb}, ${bgAlpha})`,
        // Brighten the border when the state has just changed so the eye
        // is drawn to recent transitions.
        border: `1px solid rgba(${rgb}, ${borderAlpha})`,
        boxShadow: fresh ? `0 0 0 1px rgba(${rgb}, 0.2)` : 'none',
        color: `rgba(${rgb}, ${textAlpha})`,
        fontFamily: '"IBM Plex Mono", monospace',
        fontSize: '0.65rem',
        letterSpacing: '0.05em',
        textTransform: 'lowercase',
      }}
    >
      <Icon sx={{ fontSize: '0.8rem' }} />
      {dimension && (
        <Box
          component="span"
          sx={{
            opacity: 0.55,
            fontWeight: 500,
            // Slight separator dot so the dimension reads as a label
            // rather than as part of the state name.
            '&::after': { content: '"·"', ml: 0.25 },
          }}
        >
          {dimension}
        </Box>
      )}
      {state || 'unknown'}
      {fresh && (
        <Box
          component="span"
          sx={{
            ml: 0.5,
            fontSize: '0.55rem',
            fontWeight: 600,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            opacity: 0.9,
          }}
        >
          new
        </Box>
      )}
    </Box>
  );
};

// Compact pill that flags a recent custom event fire on the symbol.
const CustomEventChip = ({ name }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: 1,
      py: 0.25,
      backgroundColor: 'var(--dwlf-accent-08)',
      border: '1px solid var(--dwlf-accent-40)',
      color: 'var(--dwlf-accent)',
      fontFamily: '"IBM Plex Mono", monospace',
      fontSize: '0.65rem',
      letterSpacing: '0.05em',
      textTransform: 'lowercase',
      maxWidth: 180,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}
    title={name}
  >
    <Box component="span" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>
      ●
    </Box>
    {name}
  </Box>
);

const SymbolRow = ({ symbol, data }) => {
  const price = data?.price?.close;
  const changePct = data?.price?.change?.pct;
  const changeColor =
    changePct == null
      ? 'var(--dwlf-ink-50)'
      : changePct >= 0
        ? '#3fb950'
        : '#f85149';

  const trend = data?.regime?.trend;
  const momentum = data?.regime?.momentum;
  const cycle = data?.regime?.cycle;

  // Custom events fired on this symbol within the fresh window. The briefing
  // returns up to 10 in `activeEvents`; we filter and dedupe to keep the row
  // legible.
  const recentCustomEvents = (data?.activeEvents || [])
    .filter((e) => isFresh(e.firedAt || e.date))
    .reduce((acc, e) => {
      const name = e.type || e.name || e.eventName;
      if (!name) {return acc;}
      if (!acc.find((x) => x.name === name)) {acc.push({ name, firedAt: e.firedAt });}
      return acc;
    }, [])
    .slice(0, 3);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '80px 1fr', md: '90px 110px 1fr' },
        alignItems: 'center',
        gap: 2,
        py: 1.5,
        borderBottom: '1px solid var(--dwlf-ink-04)',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Space Mono", monospace',
          fontSize: '0.85rem',
          fontWeight: 500,
          color: 'var(--dwlf-text)',
          letterSpacing: '0.02em',
        }}
      >
        {symbol}
      </Typography>
      <Box>
        {price != null && (
          <Typography
            sx={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.8rem',
              color: 'var(--dwlf-ink-85)',
            }}
          >
            ${price < 10 ? price.toFixed(4) : price.toFixed(2)}
          </Typography>
        )}
        {changePct != null && (
          <Typography
            sx={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.7rem',
              color: changeColor,
            }}
          >
            {changePct >= 0 ? '+' : ''}
            {changePct.toFixed(2)}%
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 0.75,
          flexWrap: 'wrap',
          alignItems: 'center',
          // On xs the row is a 2-col grid (symbol | price/change). The badges
          // are the 3rd child, so without spanning they'd land in the 80px
          // symbol column on a second row and overflow. Span both columns to
          // get a stacked layout on mobile.
          gridColumn: { xs: '1 / -1', md: '3 / -1' },
          mt: { xs: 1, md: 0 },
        }}
      >
        {trend?.state && (
          <TrendBadge dimension="trend" state={trend.state} fresh={isFresh(trend.since)} />
        )}
        {momentum?.state && (
          <TrendBadge dimension="mom" state={momentum.state} fresh={isFresh(momentum.since)} />
        )}
        {cycle?.state && (
          <TrendBadge dimension="cycle" state={cycle.state} fresh={isFresh(cycle.since)} />
        )}
        {/* Regime data hasn't been computed for this symbol yet. Show an
            inline "pending" note so the row doesn't look broken — the
            user still sees the price and any custom events. */}
        {!trend?.state && !momentum?.state && !cycle?.state && (
          <Typography
            sx={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.65rem',
              color: 'var(--dwlf-ink-35)',
              fontStyle: 'italic',
            }}
          >
            regime data pending — check back soon
          </Typography>
        )}
        {recentCustomEvents.map((evt) => (
          <CustomEventChip key={evt.name} name={evt.name} />
        ))}
      </Box>
    </Box>
  );
};

// Plain-language explainer for the three regime models. Triggered from
// the panel header help icon. Avoids "finite state machine" jargon —
// uses trader-friendly framing so the badges feel intuitive even for
// users who haven't read any docs yet.
const RegimeExplainerDialog = ({ open, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        backgroundColor: 'var(--dwlf-surface)',
        border: '1px solid var(--dwlf-ink-12)',
        borderRadius: 0,
        backgroundImage: 'none',
      },
    }}
  >
    <DialogTitle
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        px: { xs: 3, md: 4 },
        pt: 3,
        pb: 1,
      }}
    >
      <Box>
        <Typography
          sx={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.6rem',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'var(--dwlf-accent-60)',
            mb: 0.5,
          }}
        >
          How to read this
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Space Grotesk", sans-serif',
            fontSize: '1.25rem',
            fontWeight: 500,
            color: 'var(--dwlf-text)',
            letterSpacing: '-0.01em',
          }}
        >
          The three regime models
        </Typography>
      </Box>
      <IconButton
        onClick={onClose}
        size="small"
        sx={{ color: 'var(--dwlf-ink-50)', '&:hover': { color: 'var(--dwlf-text)' } }}
      >
        <Close sx={{ fontSize: '1.1rem' }} />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ px: { xs: 3, md: 4 }, pb: 3 }}>
      <Typography
        sx={{
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.8rem',
          color: 'var(--dwlf-ink-70)',
          lineHeight: 1.7,
          mb: 2.5,
        }}
      >
        For each symbol on your watchlist DWLF runs three independent regime
        models. Each model sits in a state until conditions change. When a
        state flips, you&apos;ll see a <strong>NEW</strong> tag on the badge —
        that&apos;s usually worth a closer look.
      </Typography>

      {[
        {
          label: 'trend·',
          title: 'Trend',
          body:
            'Where the market is heading on the daily timeframe. Reads from the EMA stack — bullish when fast/slow EMAs are stacked up, bearish when stacked down. Slow to flip; designed to keep you on the right side of the major move.',
        },
        {
          label: 'mom·',
          title: 'Momentum',
          body:
            'How forcefully price is moving right now. Reads from a smoothed stochastic — flips between oversold, neutral, and overbought, with bullish/bearish signal states for actionable inflections. Faster than the trend model; useful for entries.',
        },
        {
          label: 'cycle·',
          title: 'Cycle phase',
          body:
            'Where you are in the current swing — bottoming, rising, topping, or declining. Reads from weekly structure (higher highs, lower lows). Slowest of the three; tells you whether the broader cycle is for or against your trade.',
        },
      ].map(({ label, title, body }) => (
        <Box key={title} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
            <Typography
              sx={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.7rem',
                color: 'var(--dwlf-accent-70)',
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Space Grotesk", sans-serif',
                fontSize: '0.95rem',
                fontWeight: 500,
                color: 'var(--dwlf-text)',
              }}
            >
              {title}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.78rem',
              color: 'var(--dwlf-ink-60)',
              lineHeight: 1.7,
            }}
          >
            {body}
          </Typography>
        </Box>
      ))}

      <Typography
        sx={{
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.78rem',
          color: 'var(--dwlf-ink-55)',
          lineHeight: 1.7,
          mt: 1,
        }}
      >
        The cyan pills after the regime badges (●) are recent custom event
        fires — alerts you set up that triggered in the last few days.
      </Typography>
    </DialogContent>
  </Dialog>
);

// Compact relative-day label for calendar events: Today / Tomorrow / a
// weekday for the next week / a short date beyond that.
const relDayLabel = (daysUntil, dateStr) => {
  if (daysUntil === 0) {return 'Today';}
  if (daysUntil === 1) {return 'Tomorrow';}
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) {return `in ${daysUntil}d`;}
  if (daysUntil <= 6) {return d.toLocaleDateString('en-GB', { weekday: 'short' });}
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const BriefingPanel = forwardRef(({ onAddDefaults, addingDefaults }, ref) => {
  const dispatch = useDispatch();
  const [explainerOpen, setExplainerOpen] = useState(false);

  // Briefing data is now lifted to Redux so other consumers (RegimeHeatmap)
  // can read the same payload without a duplicate fetch.
  const briefing = useSelector((s) => s.markets.briefingData);
  const briefingLoading = useSelector((s) => s.markets.briefingLoading);
  const briefingError = useSelector((s) => s.markets.briefingError);
  const briefingLastFetched = useSelector((s) => s.markets.briefingLastFetched);
  // We only treat the panel as "loaded" once a fetch has resolved at least
  // once (success OR failure). Otherwise the briefingLoading flag flapping
  // briefly back to false between TTL-skipped refreshes would leak through
  // the skeleton.
  const briefingResolved =
    briefingLastFetched != null || (briefingError !== null && briefingError !== undefined);
  const loading = briefingLoading || !briefingResolved;
  const error = briefingError;

  // Source of truth for whether the user actually has a watchlist. The
  // briefing API response can be empty for reasons unrelated to watchlist
  // state (backend hasn't computed yet, processing delay, partial outage),
  // so we read the watchlist directly from Redux to decide whether to show
  // the empty-state CTA vs a "briefing not ready" message.
  //
  // We also need to know whether the watchlist fetch has *resolved* yet —
  // the initial Redux state is `[]`, so without this gate a user with an
  // existing watchlist would briefly see the "use suggested defaults" CTA
  // if the briefing API resolves before fetchWatchlist does.
  //
  // We treat the watchlist as "ready" once it has either succeeded
  // (`watchlistLastFetched` set) or errored (`watchlistError` set). Without
  // the error branch, a rejected fetch would leave `ready` permanently
  // false and trap the panel in an infinite skeleton.
  const watchlist = useSelector((s) => s.markets.watchlist || []);
  const watchlistLoading = useSelector((s) => s.markets.watchlistLoading);
  const watchlistLastFetched = useSelector((s) => s.markets.watchlistLastFetched);
  const watchlistError = useSelector((s) => s.markets.watchlistError);
  // "Ready" means the fetch has resolved one way or the other — used to
  // unblock the skeleton. Without the error branch a rejected fetch would
  // leave the panel stuck loading forever.
  const watchlistReady =
    !watchlistLoading && (watchlistLastFetched != null || watchlistError != null);
  // We only show the "your watchlist is empty" CTA when we have *positive*
  // confirmation: the fetch succeeded and returned no symbols. A cold
  // failure leaves `watchlistLastFetched` null, and we'd rather show the
  // generic "briefing not ready" branch than mislead the user into adding
  // duplicates.
  const watchlistEmpty = watchlistLastFetched != null && watchlist.length === 0;

  const refresh = useCallback(() => {
    dispatch(fetchBriefing({ force: true }));
  }, [dispatch]);

  useImperativeHandle(ref, () => ({ refresh }), [refresh]);

  // Initial load — the thunk's condition guard skips if a fetch is in
  // flight or the cached data is fresh, so this is safe to dispatch on
  // every mount without piling up duplicate calls.
  useEffect(() => {
    dispatch(fetchBriefing());
  }, [dispatch]);

  const symbols = briefing?.symbols ? Object.entries(briefing.symbols) : [];
  // Strip the "unknown" sector bucket — it's symbols the backend couldn't
  // map to a sector and doesn't tell the user anything actionable
  // ("UNKNOWN · neutral" just looks broken).
  const sectorSentimentEntries = Object.entries(
    briefing?.crossAsset?.sectorSentiment || {},
  ).filter(([sector]) => sector && sector.toLowerCase() !== 'unknown');
  // Forward macro calendar (crossAsset.upcoming). Already in the briefing
  // payload — no extra fetch. Each event may carry a `confluence` list of
  // symbols at a cycle decision point into it (the DWLF-only signal).
  const upcoming = briefing?.crossAsset?.upcoming;
  const upcomingEvents = Array.isArray(upcoming?.events) ? upcoming.events : [];
  // Treat the panel as still loading if either feed hasn't resolved — the
  // empty-state branches both depend on knowing the true watchlist state,
  // so showing them before the watchlist has loaded would be incorrect.
  // An error always wins, so we never render the skeleton and the error
  // banner at the same time.
  const stillLoading = !error && (loading || (symbols.length === 0 && !watchlistReady));
  const symbolCount = stillLoading ? null : `${symbols.length} SYMBOLS`;

  return (
    <Panel
      eyebrow="Daily briefing"
      title="Today's intelligence"
      count={symbolCount}
      action={
        <IconButton
          size="small"
          onClick={() => setExplainerOpen(true)}
          aria-label="How to read this"
          sx={{
            color: 'var(--dwlf-ink-40)',
            p: 0.5,
            '&:hover': { color: 'var(--dwlf-accent)' },
          }}
        >
          <HelpOutline sx={{ fontSize: '1rem' }} />
        </IconButton>
      }
    >
      <RegimeExplainerDialog open={explainerOpen} onClose={() => setExplainerOpen(false)} />
      {stillLoading && (
        <Box>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={48}
              sx={{
                mb: 1,
                bgcolor: 'var(--dwlf-ink-03)',
              }}
            />
          ))}
        </Box>
      )}

      {error && (
        <Typography
          sx={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.8rem',
            color: 'rgba(248, 81, 73, 0.8)',
          }}
        >
          {error}
        </Typography>
      )}

      {!stillLoading && !error && symbols.length === 0 && watchlistEmpty && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2,
            py: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: '1rem',
              fontWeight: 500,
              color: 'var(--dwlf-text)',
            }}
          >
            Your watchlist is empty.
          </Typography>
          <Typography
            sx={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.8rem',
              color: 'var(--dwlf-ink-55)',
              lineHeight: 1.7,
              maxWidth: 360,
            }}
          >
            Add some symbols to see DWLF&apos;s structured intelligence —
            regime classification, recent events, and key levels for each.
          </Typography>
          {onAddDefaults && (
            <Box
              component="button"
              onClick={onAddDefaults}
              disabled={addingDefaults}
              sx={{
                mt: 1,
                px: 2.5,
                py: 1,
                backgroundColor: 'transparent',
                border: '1px solid var(--dwlf-accent-40)',
                color: 'var(--dwlf-accent)',
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.7rem',
                fontWeight: 500,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover:not(:disabled)': {
                  backgroundColor: 'var(--dwlf-accent-08)',
                  borderColor: 'var(--dwlf-accent)',
                },
                '&:disabled': {
                  opacity: 0.5,
                  cursor: 'not-allowed',
                },
              }}
            >
              {addingDefaults ? 'Adding...' : 'Use suggested defaults →'}
            </Box>
          )}
        </Box>
      )}

      {/* Watchlist exists but the briefing came back empty — likely a
          backend processing delay rather than user state. Don't offer the
          "add defaults" CTA here, since adding more would compound the
          problem. */}
      {!stillLoading && !error && symbols.length === 0 && !watchlistEmpty && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 1.5,
            py: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: '1rem',
              fontWeight: 500,
              color: 'var(--dwlf-text)',
            }}
          >
            Briefing not ready yet.
          </Typography>
          <Typography
            sx={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.8rem',
              color: 'var(--dwlf-ink-55)',
              lineHeight: 1.7,
              maxWidth: 360,
            }}
          >
            {watchlistLastFetched != null ? (
              <>
                DWLF is still computing intelligence for your{' '}
                {watchlist.length === 1 ? 'symbol' : `${watchlist.length} symbols`}.
              </>
            ) : (
              <>DWLF is still computing intelligence for your watchlist.</>
            )}
            {' '}Try refreshing in a moment.
          </Typography>
        </Box>
      )}

      {!stillLoading && !error && symbols.length > 0 && (
        <>
          <Box>
            {symbols.map(([symbol, data]) => (
              <SymbolRow key={symbol} symbol={symbol} data={data} />
            ))}
          </Box>

          <BriefingDepth depth={briefing?.crossAsset?.depth} />

          {sectorSentimentEntries.length > 0 && (
            <Box
              sx={{
                mt: 3,
                pt: 3,
                borderTop: '1px solid var(--dwlf-ink-06)',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: '0.65rem',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: 'var(--dwlf-ink-40)',
                  mb: 0.75,
                }}
              >
                Sector sentiment
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: '0.7rem',
                  color: 'var(--dwlf-ink-50)',
                  lineHeight: 1.6,
                  mb: 1.5,
                  maxWidth: 520,
                }}
              >
                How each tracked sector is trending, aggregated from the
                regime states of its constituents. Gives you a quick read
                on whether risk is on or off across the broader market.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {sectorSentimentEntries.map(([sector, info]) => (
                  <Chip
                    key={sector}
                    label={`${sector.toUpperCase()} · ${info?.trend || 'unknown'}`}
                    size="small"
                    sx={{
                      borderRadius: 0,
                      backgroundColor: 'var(--dwlf-ink-04)',
                      border: '1px solid var(--dwlf-ink-08)',
                      color: 'var(--dwlf-ink-60)',
                      fontFamily: '"IBM Plex Mono", monospace',
                      fontSize: '0.65rem',
                      letterSpacing: '0.05em',
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {upcomingEvents.length > 0 && (
            <Box
              sx={{
                mt: 3,
                pt: 3,
                borderTop: '1px solid var(--dwlf-ink-06)',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: '0.65rem',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: 'var(--dwlf-ink-40)',
                  mb: 0.75,
                }}
              >
                Upcoming macro events
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: '0.7rem',
                  color: 'var(--dwlf-ink-50)',
                  lineHeight: 1.6,
                  mb: 1.5,
                  maxWidth: 520,
                }}
              >
                Scheduled high-impact events in the next {upcoming?.windowDays ?? 14} days.
                Symbols under an event are at a cycle decision point into it — a
                catalyst landing into a potential turn.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {upcomingEvents.map((ev) => {
                  const isHigh = ev.impact === 'high';
                  const accent = isHigh ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-45)';
                  const shown = Array.isArray(ev.confluence) ? ev.confluence.slice(0, 8) : [];
                  const extra = (ev.confluence?.length || 0) - shown.length;
                  return (
                    <Box key={`${ev.date}-${ev.event}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={relDayLabel(ev.daysUntil, ev.date)}
                          size="small"
                          sx={{
                            borderRadius: 0,
                            backgroundColor: isHigh ? 'var(--dwlf-accent-12)' : 'var(--dwlf-ink-04)',
                            border: `1px solid ${isHigh ? 'var(--dwlf-accent-40)' : 'var(--dwlf-ink-08)'}`,
                            color: accent,
                            fontFamily: '"IBM Plex Mono", monospace',
                            fontSize: '0.65rem',
                            letterSpacing: '0.05em',
                            fontWeight: 600,
                          }}
                        />
                        <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.75rem', color: 'var(--dwlf-ink-85)' }}>
                          {ev.event}
                        </Typography>
                        <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.65rem', color: 'var(--dwlf-ink-35)' }}>
                          {ev.region}{ev.time ? ` · ${ev.time}` : ''}
                        </Typography>
                      </Box>
                      {shown.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', pl: 0.5 }}>
                          {shown.map((c) => (
                            <Chip
                              key={c.symbol}
                              label={c.symbol}
                              size="small"
                              title={(c.flags || []).join(', ')}
                              sx={{
                                borderRadius: 0,
                                backgroundColor: 'rgba(34, 197, 94, 0.10)',
                                border: '1px solid rgba(34, 197, 94, 0.3)',
                                color: '#22c55e',
                                fontFamily: '"IBM Plex Mono", monospace',
                                fontSize: '0.6rem',
                                letterSpacing: '0.04em',
                              }}
                            />
                          ))}
                          {extra > 0 && (
                            <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.6rem', color: 'var(--dwlf-ink-35)', alignSelf: 'center' }}>
                              +{extra} more
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </>
      )}
    </Panel>
  );
});

BriefingPanel.displayName = 'BriefingPanel';

export default BriefingPanel;
